import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, ShieldAlert, ShoppingCart, CheckCircle2, X, Package, PenLine, BadgeCheck, Send, Download, RotateCw } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import { PbrMark, PbrNotice } from '../../components/cultivar';
import cultivarService from '../../services/cultivar';
import {
  CULTIVAR_ORDER_STATUS_LABELS,
  CULTIVAR_CONTRACT_ROLE_LABELS,
  CULTIVAR_APPROVER_ROLE_LABELS,
  type CultivarOrder,
} from '../../types/cultivar';
import { useModuleLicense } from '../../hooks/useModuleLicense';

const STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-blue-100 text-blue-800',
  nursery_accepted: 'bg-indigo-100 text-indigo-800',
  awaiting_approvers: 'bg-amber-100 text-amber-800',
  awaiting_customer: 'bg-amber-100 text-amber-800',
  customer_signed: 'bg-emerald-100 text-emerald-800',
  confirmed: 'bg-emerald-100 text-emerald-800',
  fulfilled: 'bg-teal-100 text-teal-800',
  cancelled: 'bg-red-100 text-red-700',
};

const DOC_STATUS_BADGE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-amber-100 text-amber-800',
  signed: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-700',
  void: 'bg-gray-100 text-gray-500',
};

function fmtMoney(v: string | number | null, currency: string): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `${currency || 'AUD'} ${n.toFixed(2)}`;
}

function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const orderId = id ? parseInt(id, 10) : NaN;
  const navigate = useNavigate();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('cultivar', ['pbr', 'admin']);

  const [order, setOrder] = useState<CultivarOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancel, setShowCancel] = useState(false);

  const load = useCallback(async () => {
    if (!Number.isFinite(orderId)) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await cultivarService.getOrderingOrder(orderId);
      if (resp.success && resp.data) setOrder(resp.data as CultivarOrder);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (
    label: string,
    fn: () => Promise<{ success?: boolean; data?: unknown; error?: { message?: string } }>
  ) => {
    setActionLoading(label);
    setError(null);
    setInfo(null);
    try {
      const resp = await fn();
      if (!resp.success) throw new Error(resp.error?.message || `${label} failed`);
      setInfo(`${label} succeeded.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed`);
    } finally {
      setActionLoading(null);
    }
  };

  if (licenseLoading || loading) {
    return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!hasAccess) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6 flex items-start gap-3">
          <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold mb-1">Cultivar PBR seat required</h3>
          </div>
        </CardContent></Card>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card><CardContent className="p-6">
          <p className="text-gray-700">{error || 'Order not found.'}</p>
          <Button variant="secondary" onClick={() => navigate('/cultivar/orders')} className="mt-3">
            <ArrowLeft className="w-4 h-4 mr-2" /> Orders
          </Button>
        </CardContent></Card>
      </div>
    );
  }

  const canCancel = !['confirmed', 'fulfilled', 'cancelled'].includes(order.status);

  return (
    <>
      <PageHeader
        title={`Order #${order.id} — ${order.cultivar_name || ''}`}
        subtitle={order.customer_company ? `For ${order.customer_company}` : undefined}
        icon={<ShoppingCart className="h-6 w-6 text-white" />}
      />
      <div className="max-w-5xl mx-auto p-6 space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => navigate('/cultivar/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" /> Orders
          </Button>
          {order.status === 'submitted' && (
            <Button onClick={() => runAction('Nursery accept', () => cultivarService.nurseryAcceptOrder(order.id))}
              disabled={actionLoading !== null}>
              {actionLoading === 'Nursery accept' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PenLine className="w-4 h-4 mr-2" />}
              Accept &amp; send contracts
            </Button>
          )}
          {['nursery_accepted', 'awaiting_customer', 'awaiting_approvers'].includes(order.status) && (order.documents || []).some((d) => d.status === 'draft') && (
            <Button variant="secondary"
              onClick={() => runAction('Retry dispatch', () => cultivarService.retryDispatchOrder(order.id))}
              disabled={actionLoading !== null}>
              {actionLoading === 'Retry dispatch' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCw className="w-4 h-4 mr-2" />}
              Retry dispatch
            </Button>
          )}
          {order.status === 'confirmed' && (
            <Button onClick={() => runAction('Mark fulfilled', () => cultivarService.markOrderFulfilled(order.id))}
              disabled={actionLoading !== null}>
              {actionLoading === 'Mark fulfilled' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Package className="w-4 h-4 mr-2" />}
              Mark fulfilled
            </Button>
          )}
          {canCancel && (
            <Button variant="danger" onClick={() => setShowCancel(true)} disabled={actionLoading !== null}>
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          )}
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-800 rounded px-4 py-2 text-sm">{error}</div>}
        {info && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded px-4 py-2 text-sm">{info}</div>}

        <div className="grid md:grid-cols-2 gap-4">
          <Card>
            <CardContent className="p-4 space-y-2">
              <h3 className="font-semibold mb-1">Status</h3>
              <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[order.status] || 'bg-gray-100'}`}>
                {CULTIVAR_ORDER_STATUS_LABELS[order.status] || order.status}
              </span>
              {order.confirmed_at && <p className="text-xs text-gray-500">Confirmed {order.confirmed_at.slice(0, 10)}</p>}
              {order.fulfilled_at && <p className="text-xs text-gray-500">Fulfilled {order.fulfilled_at.slice(0, 10)}</p>}
              {order.cancelled_at && <p className="text-xs text-gray-500">Cancelled {order.cancelled_at.slice(0, 10)}{order.cancellation_reason ? ` — ${order.cancellation_reason}` : ''}</p>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-1">
              <h3 className="font-semibold mb-1">Pricing (frozen)</h3>
              <p className="text-sm">Unit: <span className="font-medium">{fmtMoney(order.frozen_unit_price, order.currency)}</span></p>
              <p className="text-sm">Total: <span className="font-medium">{fmtMoney(order.frozen_total, order.currency)}</span></p>
              <p className="text-sm">Annual fee per tree: <span className="font-medium">{fmtMoney(order.frozen_annual_fee, order.currency)}</span></p>
              {order.expected_payment_date && <p className="text-xs text-gray-500">Payment expected by {order.expected_payment_date}</p>}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-1">
              <h3 className="font-semibold mb-1">Cultivar</h3>
              <p className="text-sm">
                {order.cultivar_name}
                <PbrMark status={order.cultivar_protection_status ?? null} />
                {order.cultivar_trade_name ? ` (${order.cultivar_trade_name})` : ''}
              </p>
              <p className="text-sm">Qty: <span className="font-medium">{order.qty_trees}</span></p>
              {(order.sale_window_start || order.sale_window_end) && (
                <p className="text-xs text-gray-500">Sale window {order.sale_window_start || '—'} → {order.sale_window_end || '—'}</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 space-y-1">
              <h3 className="font-semibold mb-1">Customer</h3>
              <p className="text-sm font-medium">{order.customer_company || '—'}</p>
              <p className="text-sm">{order.customer_contact} &lt;{order.customer_email}&gt;</p>
              {order.customer_phone && <p className="text-sm">{order.customer_phone}</p>}
              {order.orchard_org_name && (
                <p className="text-xs text-emerald-700 mt-1"><BadgeCheck className="inline w-3 h-3 mr-1" />Org provisioned: {order.orchard_org_name}</p>
              )}
            </CardContent>
          </Card>
        </div>

        {order.approvals && order.approvals.length > 0 && (
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-3 border-b">
                <h3 className="font-semibold">Approvals</h3>
                <p className="text-xs text-gray-500">Marketing / distribution sign-off before contracts go to the customer.</p>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">Approver</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Required</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2">Decided at</th>
                    <th className="px-4 py-2">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {order.approvals.map((a) => (
                    <tr key={a.id} className="border-t">
                      <td className="px-4 py-3">{a.approver_org_name || `Org #${a.approver_org_id}`}</td>
                      <td className="px-4 py-3">{CULTIVAR_APPROVER_ROLE_LABELS[a.approver_role]}</td>
                      <td className="px-4 py-3">{a.required ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${
                        a.status === 'approved' ? 'bg-emerald-100 text-emerald-800' :
                        a.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        a.status === 'skipped' ? 'bg-gray-100 text-gray-500' :
                        'bg-amber-100 text-amber-800'
                      }`}>{a.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{a.decided_at ? a.decided_at.slice(0, 10) : '—'}</td>
                      <td className="px-4 py-3 text-gray-600 max-w-xs truncate" title={a.notes || ''}>{a.notes || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            <div className="px-4 py-3 border-b">
              <h3 className="font-semibold">Contract documents</h3>
              <p className="text-xs text-gray-500">Sent to the customer for digital signature via Adobe Sign.</p>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">Role</th>
                  <th className="px-4 py-2">Required</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Signer</th>
                  <th className="px-4 py-2">Signed at</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {(!order.documents || order.documents.length === 0) && (
                  <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">No documents dispatched yet.</td></tr>
                )}
                {order.documents?.map((d) => {
                  const docActionKey = `doc-${d.id}`;
                  const canRemind = d.envelope_id !== null && d.status === 'sent';
                  const canDownload = d.status === 'signed' && !!d.signed_pdf_path;
                  return (
                    <tr key={d.id} className="border-t">
                      <td className="px-4 py-3">{CULTIVAR_CONTRACT_ROLE_LABELS[d.role] || d.role}</td>
                      <td className="px-4 py-3">{d.required ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${DOC_STATUS_BADGE[d.status] || 'bg-gray-100'}`}>
                          {d.status === 'signed' && <CheckCircle2 className="inline w-3 h-3 mr-1" />}
                          {d.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">{d.signer_email || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{d.envelope_signed_at ? d.envelope_signed_at.slice(0, 10) : ''}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {canRemind && (
                          <Button variant="ghost" size="sm" title="Send signing reminder"
                            onClick={() => runAction(`Remind ${d.role}`, () => cultivarService.remindOrderDocument(order.id, d.id))}
                            disabled={actionLoading !== null}>
                            {actionLoading === `Remind ${d.role}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </Button>
                        )}
                        {canDownload && (
                          <Button variant="ghost" size="sm" title="Download signed PDF"
                            onClick={async () => {
                              setActionLoading(docActionKey);
                              try {
                                const ok = await cultivarService.downloadOrderDocumentSignedPdf(order.id, d.id, `order-${order.id}-${d.role}.pdf`);
                                if (!ok) setError('Signed PDF download failed.');
                              } finally {
                                setActionLoading(null);
                              }
                            }}
                            disabled={actionLoading !== null}>
                            {actionLoading === docActionKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {(!order.cultivar_protection_status ||
          order.cultivar_protection_status === 'pending' ||
          order.cultivar_protection_status === 'protected') && (
          <PbrNotice />
        )}

        {showCancel && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-semibold">Cancel order #{order.id}?</h2>
              </div>
              <div className="px-6 py-4">
                <label className="text-sm block">
                  <span className="text-gray-700">Reason (optional)</span>
                  <textarea value={cancelReason} rows={3}
                    onChange={(e) => setCancelReason(e.target.value)}
                    className="mt-1 w-full border rounded px-2 py-1.5" />
                </label>
              </div>
              <div className="px-6 py-3 border-t flex justify-end gap-2 bg-gray-50">
                <Button variant="secondary" onClick={() => setShowCancel(false)} disabled={actionLoading !== null}>Keep</Button>
                <Button variant="danger"
                  onClick={async () => {
                    await runAction('Cancel', () => cultivarService.cancelOrderingOrder(order.id, cancelReason));
                    setShowCancel(false);
                  }}
                  disabled={actionLoading !== null}>
                  {actionLoading === 'Cancel' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Cancel order
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default OrderDetail;
